import { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Phone, Globe, Loader2, ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import PageHeader from '../../components/PageHeader';

interface Prospect {
  id: number;
  nombre: string;
  email: string;
  web: string | null;
  telefono: string | null;
  isla: string | null;
  municipio: string | null;
  tipo_fuente: string | null;
  sequence_type: 'A' | 'B' | 'C';
  status: string;
  last_sent_at: string | null;
}

interface Note {
  id: number;
  note: string;
  author: string;
  created_at: string;
}

interface SendHistoryRow {
  step: number;
  subject: string;
  sent_at: string;
  delivery_status: string;
}

const CRM_STATUSES = [
  { value: 'replied', label: '📩 Respondió' },
  { value: 'interested', label: '⭐ Interesado' },
  { value: 'meeting_booked', label: '📅 Reunión agendada' },
  { value: 'closed', label: '✅ Cerrado' },
  { value: 'done', label: '⬜ Finalizar secuencia' },
  { value: 'skipped', label: '⏭ Saltar' },
  { value: 'unsubscribed', label: '🚫 Baja manual' },
];

function ProspectCRMCard({ prospect, onUpdate }: {
  prospect: Prospect;
  onUpdate: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [sendHistory, setSendHistory] = useState<SendHistoryRow[]>([]);
  const [newNote, setNewNote] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const loadDetails = useCallback(async () => {
    const res = await fetch(`/api/outreach/notes?prospect_id=${prospect.id}`);
    const data = await res.json() as { notes: Note[]; send_history: SendHistoryRow[] };
    setNotes(data.notes ?? []);
    setSendHistory(data.send_history ?? []);
  }, [prospect.id]);

  useEffect(() => {
    if (expanded) loadDetails();
  }, [expanded, loadDetails]);

  const saveNote = async () => {
    if (!newNote.trim() && !newStatus) return;
    setSaving(true);
    try {
      await fetch('/api/outreach/notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prospect_id: prospect.id,
          note: newNote || `Status changed to ${newStatus}`,
          new_status: newStatus || undefined,
        }),
      });
      setNewNote('');
      setNewStatus('');
      await loadDetails();
      if (newStatus) onUpdate();
    } finally {
      setSaving(false);
    }
  };

  const statusColor: Record<string, string> = {
    replied: 'bg-blue-100 text-blue-700',
    interested: 'bg-green-100 text-green-700',
    meeting_booked: 'bg-purple-100 text-purple-700',
    closed: 'bg-green-200 text-green-800',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{prospect.nombre}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[prospect.status] ?? 'bg-gray-100 text-gray-500'}`}>
              {prospect.status}
            </span>
          </div>
          <div className="text-sm text-gray-500 flex gap-3 mt-0.5 flex-wrap">
            <span>{prospect.email}</span>
            {prospect.telefono && <span>{prospect.telefono}</span>}
            {prospect.isla && <span>{prospect.municipio ?? prospect.isla}</span>}
          </div>
        </div>
        {expanded ? <ChevronUp size={16} className="text-gray-400 shrink-0" /> : <ChevronDown size={16} className="text-gray-400 shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {/* Quick actions */}
          <div className="flex gap-2 flex-wrap">
            <a href={`mailto:${prospect.email}`}
              className="btn-secondary text-sm flex items-center gap-1.5 px-3 py-1.5">
              <MessageSquare size={13} /> Email
            </a>
            {prospect.telefono && (
              <a href={`tel:${prospect.telefono}`}
                className="btn-secondary text-sm flex items-center gap-1.5 px-3 py-1.5">
                <Phone size={13} /> Llamar
              </a>
            )}
            {prospect.web && (
              <a href={prospect.web} target="_blank" rel="noopener"
                className="btn-secondary text-sm flex items-center gap-1.5 px-3 py-1.5">
                <Globe size={13} /> Web
              </a>
            )}
          </div>

          {/* Send history */}
          {sendHistory.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Emails enviados</p>
              <div className="space-y-1">
                {sendHistory.map((row, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-14 text-xs text-gray-400">Paso {row.step}</span>
                    <span className="flex-1 text-gray-600 truncate">{row.subject}</span>
                    <span className="text-xs text-gray-400">{new Date(row.sent_at).toLocaleDateString('es')}</span>
                    <span className={`text-xs ${row.delivery_status === 'delivered' ? 'text-green-500' : 'text-gray-400'}`}>
                      {row.delivery_status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {notes.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Notas</p>
              <div className="space-y-2">
                {notes.map(n => (
                  <div key={n.id} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-700">{n.note}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {n.author} · {new Date(n.created_at).toLocaleDateString('es')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add note / change status */}
          <div className="border-t border-gray-100 pt-3 space-y-2">
            <textarea
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              placeholder="Añadir nota..."
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
            />
            <div className="flex gap-2">
              <select
                value={newStatus}
                onChange={e => setNewStatus(e.target.value)}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Cambiar estado (opcional)</option>
                {CRM_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <button
                onClick={saveNote}
                disabled={saving || (!newNote.trim() && !newStatus)}
                className="btn-primary px-4 py-2 text-sm flex items-center gap-1.5 disabled:opacity-40"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : null}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OutreachCRMPage() {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('replied');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  const fetchProspects = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ status: statusFilter, page: String(page), limit: String(LIMIT) });
    try {
      const res = await fetch(`/api/outreach/prospects?${params}`);
      const data = await res.json() as { prospects: Prospect[]; total: number };
      setProspects(data.prospects ?? []);
      setTotal(data.total ?? 0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        type="outreach"
        icon={MessageCircle}
        title="CRM Outreach"
        subtitle={`${total} leads en "${statusFilter}"`}
      />

      <div className="flex gap-2 flex-wrap mb-4">
        {[
          { v: 'replied', l: '📩 Respondieron' },
          { v: 'interested', l: '⭐ Interesados' },
          { v: 'meeting_booked', l: '📅 Reuniones' },
          { v: 'closed', l: '✅ Cerrados' },
        ].map(({ v, l }) => (
          <button
            key={v}
            onClick={() => { setStatusFilter(v); setPage(1); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${statusFilter === v ? 'bg-teal-700 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-teal-300'}`}
          >
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={24} className="animate-spin text-teal-500" />
        </div>
      ) : (
        <div className="space-y-3">
          {prospects.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
              No hay leads en este estado
            </div>
          )}
          {prospects.map(p => (
            <ProspectCRMCard key={p.id} prospect={p} onUpdate={fetchProspects} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 pt-4">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="btn-secondary px-4 py-2 text-sm disabled:opacity-40">← Anterior</button>
          <span className="py-2 px-3 text-sm text-gray-500">{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
            className="btn-secondary px-4 py-2 text-sm disabled:opacity-40">Siguiente →</button>
        </div>
      )}
    </div>
  );
}
